import {
    useMutation,
    UseMutationResult,
    useQuery,
    UseQueryResult,
} from 'react-query';
import { AxiosResponse } from 'axios';
import {
    withHeaders,
    instance as axiosInstance,
} from 'core/util/axiosInstance';
import { useAlert } from 'core/contexts';
import { commonShowAlert } from 'lib';
import { getDefaultHeaders } from 'core/util/other';
import { FIELD_MAPPING } from 'pages/pipelines/pipelines-helper';

const BASE_FIELD_MAPPING_URL = '/metadata/datapipeline/steps/fieldmapping';

interface FetchFieldMappingProps {
    id: string;
    token: string;
}

export interface FieldMappingStepResponse {
    id: string;
    name: string;
    description: string;
    type: 'FIELD_MAPPING';
    author: string;
    hash: string;
    timestamp: string;
    map: Record<string, string>;
}

export interface PostFieldMappingStepProps {
    name: string;
    description: string;
    map: Record<string, string | null>;
}

interface CreateFieldMappingStepProps {
    postData: PostFieldMappingStepProps;
    token: string;
}

export interface PutFieldMappingStepProps extends PostFieldMappingStepProps {
    id: string;
}

interface UpdateFieldMappingStepProps {
    putData: PutFieldMappingStepProps;
    token: string;
}

interface UpdateFieldMappingTrialRunProps {
    id: string;
    token: string;
    postData: {};
}

export const getFieldMappingData = async (
    url: string,
    token: string
): Promise<FieldMappingStepResponse> => {
    const headers = getDefaultHeaders(token);
    const response = await axiosInstance().get(url, withHeaders(headers));
    return response.data;
};

export const useFieldMapping = (
    props: FetchFieldMappingProps,
    type: string
): UseQueryResult<FieldMappingStepResponse, Error> => {
    const { id, token } = props;
    const { showAlert, hideAlert, showErrorDetails } = useAlert();
    const url = `${BASE_FIELD_MAPPING_URL}/${id}`;

    return useQuery(
        ['fieldMapping', id],
        async () => getFieldMappingData(url, token),
        {
            enabled: type === FIELD_MAPPING,
            onError: (error: any) => {
                const errorMessage: string =
                    error?.data?.message ??
                    'Unable to retrieve field mapping data';
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'error',
                    errorMessage,
                    JSON.stringify(error?.data ?? ''),
                    showErrorDetails
                );
            },
        }
    );
};

export const postFieldMapping = (
    props: CreateFieldMappingStepProps
): Promise<AxiosResponse<FieldMappingStepResponse, any>> => {
    const { postData, token } = props;
    const url = `${BASE_FIELD_MAPPING_URL}`;

    return axiosInstance().post(
        url,
        postData,
        withHeaders(getDefaultHeaders(token))
    );
};

export const useCreateFieldMapping = (): UseMutationResult<
    AxiosResponse<FieldMappingStepResponse, any>,
    Error,
    CreateFieldMappingStepProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(postFieldMapping, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Step Created.');
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to create step';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const putFieldMapping = (
    props: UpdateFieldMappingStepProps
): Promise<AxiosResponse<FieldMappingStepResponse, any>> => {
    const { putData, token } = props;
    const url = `${BASE_FIELD_MAPPING_URL}/${putData.id}`;

    return axiosInstance().put(
        url,
        putData,
        withHeaders(getDefaultHeaders(token))
    );
};

export const useUpdateFieldMapping = (): UseMutationResult<
    AxiosResponse<FieldMappingStepResponse, any>,
    Error,
    UpdateFieldMappingStepProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(putFieldMapping, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Step Updated');
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to update step';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const deleteFieldMapping = (
    props: FetchFieldMappingProps
): Promise<AxiosResponse<void, any>> => {
    const { id, token } = props;
    const url = `${BASE_FIELD_MAPPING_URL}/${id}`;

    return axiosInstance().delete(url, withHeaders(getDefaultHeaders(token)));
};

export const useDeleteFieldMapping = (): UseMutationResult<
    AxiosResponse<any, any>,
    Error,
    FetchFieldMappingProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(
        (props: FetchFieldMappingProps) => deleteFieldMapping(props),
        {
            onSuccess: () => {
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'success',
                    'Step Deleted'
                );
            },
            onError: (error: any) => {
                const errorMessage: string =
                    error?.data?.message ?? 'Unable to delete step';
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'error',
                    errorMessage,
                    JSON.stringify(error?.data ?? ''),
                    showErrorDetails
                );
            },
        }
    );
};

export const postFieldMappingTrialRun = (
    props: UpdateFieldMappingTrialRunProps
): Promise<AxiosResponse> => {
    const { id, postData, token } = props;
    const url = `${BASE_FIELD_MAPPING_URL}/${id}/trial-run`;

    return axiosInstance().post(
        url,
        postData,
        withHeaders(getDefaultHeaders(token))
    );
};

export const useFieldMappingTrialRun = (): UseMutationResult<
    AxiosResponse<any, any>,
    any,
    UpdateFieldMappingTrialRunProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(postFieldMappingTrialRun, {
        onSuccess: () => {
            commonShowAlert(
                showAlert,
                hideAlert,
                'success',
                'Trial Run Created'
            );
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to create trial run';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};
